/**
 * useDeleteRole Hook
 *
 * React Query mutation for deleting global roles.
 * Removes role assignments from users and groups.
 * Invalidates roles cache on success.
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';

/**
 * Delete role mutation
 *
 * @param tenantId - Tenant ID
 * @returns Mutation result
 */
export const useDeleteRole = (
  tenantId: string
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (roleId) => roleService.deleteRole(tenantId, roleId),
    onSuccess: () => {
      // Invalidate all roles queries for this tenant
      queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
      // Invalidate users cache (role assignments might have changed)
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
      // Invalidate groups cache (role assignments might have changed)
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
    },
    onError: (error) => {
      console.error('Failed to delete role:', error);
    },
  });
};
